<!-- =========================================================================================
    File Name: Main.vue
    Description: Main layout
    ----------------------------------------------------------------------------------------
    Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
    Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->


<template>
	<div class="layout--main" :class="[layoutTypeClass, navbarClasses, footerClasses, {'no-scroll': isAppPage}]">

		<!-- Open Customizer Button -->
		<vs-button
			size="small"
			color="primary"
			type="filled"
			class="customizer-btn"
			icon-pack="feather"
			icon="icon-more-vertical" 
			@click.stop="tableOptions=!tableOptions"/>

		<v-nav-menu
			:navMenuItems = "navMenuItems"
			title         = "Namify Dash"
			parent        = ".layout--main" />

		<div id="content-area" :class="[contentAreaClass, {'show-overlay': bodyOverlay}]">
			<div id="content-overlay" />

			<!-- Navbar -->
			<template v-if="mainLayoutType === 'horizontal' && windowWidth >= 1200">
				<the-navbar-horizontal
					:navbarType= "navbarType"
					:class="[
					{'text-white' : isNavbarDark  && !isThemeDark},
					{'text-base'  : !isNavbarDark && isThemeDark}
					]" />

				<div style="height: 62px" v-if="navbarType === 'static'"></div>

				<h-nav-menu
					:class="[
					{'text-white' : isNavbarDark  && !isThemeDark},
					{'text-base'  : !isNavbarDark && isThemeDark}
					]"
					:navMenuItems="navMenuItems" />
			</template>

			<template v-else>
				<the-navbar-vertical
					:routerTransition="routerTransition"
					:navbarColor="navbarColor"
					:class="[
					{'text-white' : isNavbarDark  && !isThemeDark},
					{'text-base'  : !isNavbarDark && isThemeDark}
					]" />
			</template>
			<!-- /Navbar -->

			<div class="content-wrapper">

				<div class="router-view">
					<div class="router-content">

						<transition :name="routerTransition">
							<!-- Table Options -->
							<div v-if="tableOptions && $route.meta.buttons" class="router-header hidden lg:flex flex-wrap vx-card p-6 items-center mb-6">

								<div>
									<div class="vs-input--label pl-0 mb-2 font-semibold">Bulk Actions:</div>
									<div class="flex flex-wrap items-center justify-between">
										<!-- BUTTONS -->
										<vs-button type="border" icon="icon-plus" icon-pack="feather" class="vs-button-custom vs-button-custom--success mr-5" icon-after :disabled="!(pageButtons['add'] && pagePermissions['add'])" @click="addPage()">Add</vs-button>
										<vs-button type="border" icon="icon-x" icon-pack="feather" class="vs-button-custom vs-button-custom--danger" icon-after disabled>Delete</vs-button>
									</div>
								</div>

								<div class="ml-auto">
									<div class="vs-input--label pl-0 mb-2 font-semibold">List Options:</div>
									<div class="flex flex-wrap items-center justify-between">
										<!-- BUTTONS -->
										<vx-tooltip text="CSV Export" position="bottom" class="mr-5" :class="{'pointer-events-none' : !(pageButtons['download'] && pagePermissions['export'])}">
											<vs-button type="border" icon="icon-download" icon-pack="feather" class="vs-button-custom" @click="downloadPage()" :disabled="!(pageButtons['download'] && pagePermissions['export'])"></vs-button>
										</vx-tooltip>
										<vx-tooltip v-if="pageButtons['refresh']" text="Reset Table" position="bottom" :class="{'pointer-events-none' : !pageButtons['refresh']}">
											<vs-button type="border" icon="icon-rotate-cw" icon-pack="feather" class="vs-button-custom" @click="refreshPage()"></vs-button>
										</vx-tooltip>
									</div>
								</div>

							</div>
						</transition>

						<div class="content-area__content">
							<back-to-top bottom="5%" :right="$vs.rtl ? 'calc(100% - 2.2rem - 38px)' : '30px'" visibleoffset="500" v-if="!hideScrollToTop">
								<vs-button icon-pack="feather" icon="icon-arrow-up" class="shadow-lg btn-back-to-top" />
							</back-to-top>

							<transition :name="routerTransition" mode="out-in">
								<router-view @changeRouteTitle="changeRouteTitle" @setAppClasses="(classesStr) => $emit('setAppClasses', classesStr)" />
							</transition>
						</div>
					</div>
				</div>
			</div>
			<the-footer />
		</div>
	</div>
</template>


<script>
	import BackToTop           from 'vue-backtotop'
	import HNavMenu            from "@/layouts/components/horizontal-nav-menu/HorizontalNavMenu.vue"
	import navMenuItems        from "@/layouts/components/vertical-nav-menu/navMenuItems.js"
	import TheNavbarHorizontal from '@/layouts/components/navbar/TheNavbarHorizontal.vue'
	import TheNavbarVertical   from '@/layouts/components/navbar/TheNavbarVertical.vue'
	import TheFooter           from '@/layouts/components/TheFooter.vue'
	import themeConfig         from '@/../themeConfig.js'
	import VNavMenu            from '@/layouts/components/vertical-nav-menu/VerticalNavMenu.vue'

	export default {
		components: {
			BackToTop,
			HNavMenu,
			TheFooter,
			TheNavbarHorizontal,
			TheNavbarVertical,
			VNavMenu
		},
		data() {
			return {
				footerType        : themeConfig.footerType  || 'static',
				hideScrollToTop   : themeConfig.hideScrollToTop,
				isNavbarDark      : false,
				navbarColor       : themeConfig.navbarColor || '#fff',
				navbarType        : themeConfig.navbarType  || 'floating',
				navMenuItems      : navMenuItems,
				routerTransition  : themeConfig.routerTransition || 'none',
				routeTitle        : this.$route.meta.pageTitle,
				tableOptions      : false,
			}
		},
		watch: {
			"$route"() {
				// Set page title
				this.routeTitle = this.$route.meta.pageTitle
			},
			isThemeDark(val) {
				const color = this.navbarColor == "#fff" && val ? "#10163a" : "#fff"
				this.updateNavbarColor(color)
			},
			"$store.state.mainLayoutType"(val) {
				this.setNavMenuVisibility(val)
			}
		},
		computed: {
			bodyOverlay() {
				return this.$store.state.bodyOverlay
			},
			contentAreaClass() {
				if(this.mainLayoutType === "vertical") {
					if      (this.verticalNavMenuWidth == "default") return "content-area-reduced"
					else if (this.verticalNavMenuWidth == "reduced") return "content-area-lg"
					else return "content-area-full"
				}
				// else if(this.mainLayoutType === "boxed") return "content-area-reduced"
				else return "content-area-full"
			},
			footerClasses() {
				return {
					'footer-hidden': this.footerType == 'hidden',
					'footer-sticky': this.footerType == 'sticky',
					'footer-static': this.footerType == 'static',
				}
			},
			isAppPage() {
				return this.$route.meta.no_scroll
			},
			isThemeDark() {
				return this.$store.state.theme == 'dark'
			},
			layoutTypeClass() {
				return `main-${this.mainLayoutType}`
			},
			mainLayoutType() {
				return this.$store.state.mainLayoutType
			},
			navbarClasses() {
				return {
					'navbar-hidden'   : this.navbarType == 'hidden',
					'navbar-sticky'   : this.navbarType == 'sticky',
					'navbar-static'   : this.navbarType == 'static',
					'navbar-floating' : this.navbarType == 'floating',
				}
			},
			verticalNavMenuWidth() {
				return this.$store.state.verticalNavMenuWidth
			},
			windowWidth() {
				return this.$store.state.windowWidth
			},
			pageButtons() {
				return {
					refresh: this.$route.meta.buttons ? this.$route.meta.buttons.refresh : false,
					download: this.$route.meta.buttons ? this.$route.meta.buttons.download : false,
					add: this.$route.meta.buttons ? this.$route.meta.buttons.add : false
				}
			},
			pagePermissions() {
				return  {
					export : this.permissionCheck(this.$route.name, 'export'),
					add   : this.permissionCheck(this.$route.name, 'add'),
					delete : this.permissionCheck(this.$route.name, 'delete')
				}
			},
		},
		methods: {
			changeRouteTitle(title) {
				this.routeTitle = title
			},
			updateNavbarColor(val) {
				this.navbarColor = val
				if (val == "#fff") this.isNavbarDark = false
				else this.isNavbarDark = true
			},
			setNavMenuVisibility(layoutType) {
				if((layoutType === 'horizontal' && this.windowWidth >= 1200) || (layoutType === "vertical" && this.windowWidth < 1200)) {
					this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', false)
					this.$store.dispatch('updateVerticalNavMenuWidth', 'no-nav-menu')
				}
				else {
					this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', true)
				}
			},
			refreshPage() {
				this.$root.$emit('refreshPage');
			},
			downloadPage() {
				this.$root.$emit('downloadPage');
			},
			addPage() {
				this.$root.$emit('addPage');
			},
		},
		created() {
			const color = this.navbarColor == "#fff" && this.isThemeDark ? "#10163a" : this.navbarColor
			this.updateNavbarColor(color)
			this.setNavMenuVisibility(this.$store.state.mainLayoutType)
		}
	}
</script>


<style lang="scss">
	.customizer-btn {
		position: fixed;
		top: calc(2.2rem + 5.5rem);
		right: 0;
		border-top-right-radius: 0 !important;
		border-bottom-right-radius: 0 !important;
		z-index: 50000;

		&.includeIconOnly.small {
			width: 24px!important;
			height: 24px!important;
		}

		// .vs-icon {
		// 	animation: spin 1.5s linear infinite;
		// }
	}
</style>

